import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function BoxForm({ box, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    dimensions: {
      length: 100,
      width: 100,
      height: 100
    },
    emptyWeightGrams: 50,
    maxWeightGrams: '',
    unitCostInCents: 100,
    usableVolumeMargin: 0.9,
    displayOrder: 0,
    internalNotes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (box) {
      setFormData({
        name: box.name || '',
        key: box.key || '',
        dimensions: {
          length: box.dimensions?.length || 100,
          width: box.dimensions?.width || 100,
          height: box.dimensions?.height || 100
        },
        emptyWeightGrams: box.emptyWeightGrams || 50,
        maxWeightGrams: box.maxWeightGrams ?? '',
        unitCostInCents: box.unitCostInCents || 100,
        usableVolumeMargin: box.usableVolumeMargin || 0.9,
        displayOrder: box.displayOrder || 0,
        internalNotes: box.internalNotes || ''
      });
    }
  }, [box]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        maxWeightGrams: formData.maxWeightGrams === '' ? null : Number(formData.maxWeightGrams),
      };
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save box');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'number' ? Number(value) : value
    });
  };

  const handleDimensionChange = (dimension, value) => {
    setFormData({
      ...formData,
      dimensions: {
        ...formData.dimensions,
        [dimension]: Number(value)
      }
    });
  };

  // Calculate volumes for display
  const totalVolumeMM3 = formData.dimensions.length * formData.dimensions.width * formData.dimensions.height;
  const usableVolumeMM3 = totalVolumeMM3 * formData.usableVolumeMargin;
  const totalVolumeCM3 = totalVolumeMM3 / 1000;
  const usableVolumeCM3 = usableVolumeMM3 / 1000;

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-2xl font-bold text-slate-900">
            {box ? 'Edit Box' : 'Add Box'}
          </h2>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={100}
                  placeholder="e.g., Small Box"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="key" className="block text-sm font-medium text-slate-700 mb-2">
                  Key *
                </label>
                <input
                  type="text"
                  id="key"
                  name="key"
                  value={formData.key}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  maxLength={40}
                  placeholder="e.g., small"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500">Unique identifier, max 40 characters</p>
              </div>
            </div>

            {/* Dimensions */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Dimensions (mm) *
              </label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Length</label>
                  <input
                    type="number"
                    value={formData.dimensions.length}
                    onChange={(e) => handleDimensionChange('length', e.target.value)}
                    required
                    disabled={loading}
                    min={1}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Width</label>
                  <input
                    type="number"
                    value={formData.dimensions.width}
                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                    required
                    disabled={loading}
                    min={1}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Height</label>
                  <input
                    type="number"
                    value={formData.dimensions.height}
                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                    required
                    disabled={loading}
                    min={1}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                  />
                </div>
              </div>
            </div>

            {/* Volume Display (Calculated) */}
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Calculated Volumes</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Total Volume:</span>
                  <span className="ml-2 font-medium text-slate-900">{totalVolumeCM3.toLocaleString()} cm³</span>
                </div>
                <div>
                  <span className="text-slate-500">Usable Volume:</span>
                  <span className="ml-2 font-medium text-slate-900">{usableVolumeCM3.toLocaleString()} cm³</span>
                </div>
              </div>
            </div>

            {/* Weight and Cost */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="emptyWeightGrams" className="block text-sm font-medium text-slate-700 mb-2">
                  Empty Weight (g) *
                </label>
                <input
                  type="number"
                  id="emptyWeightGrams"
                  name="emptyWeightGrams"
                  value={formData.emptyWeightGrams}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>

              <div>
                <label htmlFor="maxWeightGrams" className="block text-sm font-medium text-slate-700 mb-2">
                  Max Weight (g)
                </label>
                <input
                  type="number"
                  id="maxWeightGrams"
                  name="maxWeightGrams"
                  value={formData.maxWeightGrams}
                  onChange={handleChange}
                  disabled={loading}
                  min={1}
                  placeholder="Unlimited"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500">Max payload weight. Leave empty for no limit.</p>
              </div>

              <div>
                <label htmlFor="unitCostInCents" className="block text-sm font-medium text-slate-700 mb-2">
                  Unit Cost (cents) *
                </label>
                <input
                  type="number"
                  id="unitCostInCents"
                  name="unitCostInCents"
                  value={formData.unitCostInCents}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {formData.unitCostInCents ? `€${(formData.unitCostInCents / 100).toFixed(2)}` : '€0.00'}
                </p>
              </div>
            </div>

            {/* Usable Volume Margin and Display Order */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="usableVolumeMargin" className="block text-sm font-medium text-slate-700 mb-2">
                  Usable Volume Margin *
                </label>
                <input
                  type="number"
                  id="usableVolumeMargin"
                  name="usableVolumeMargin"
                  value={formData.usableVolumeMargin}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  min={0.5}
                  max={1}
                  step={0.01}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
                <p className="mt-1 text-xs text-slate-500">Between 0.5 and 1.0 (default: 0.9)</p>
              </div>

              <div>
                <label htmlFor="displayOrder" className="block text-sm font-medium text-slate-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  disabled={loading}
                  min={0}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100"
                />
              </div>
            </div>

            {/* Internal Notes */}
            <div>
              <label htmlFor="internalNotes" className="block text-sm font-medium text-slate-700 mb-2">
                Internal Notes
              </label>
              <textarea
                id="internalNotes"
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleChange}
                disabled={loading}
                rows={3}
                maxLength={500}
                placeholder="Optional internal notes about this box..."
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-100 resize-none"
              />
              <p className="mt-1 text-xs text-slate-500">Max 500 characters</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Saving...' : 'Save Box'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
